import type { ReactNode } from "react";

import { Navigate, type Location } from "react-router-dom";

import { frontendRoutes } from "@/config/frontendRoutes";
import { useAuth } from "@/state/AuthContext";

interface OnboardingGateProps {
  location: Location;
  children: ReactNode;
}

/**
 * Global onboarding gate. Teachers whose backend-confirmed onboarding is
 * incomplete are redirected into the wizard from any non-exempt route.
 *
 * This is the single source of truth for onboarding gating. It redirects at
 * render time (not from an effect) so the target route never flashes before
 * the redirect fires. It waits for `isAuthReady` and only acts on an explicit
 * `onboardingCompleted === false`, so a not-yet-resolved user (restored from
 * storage before the /api/auth/me refresh) is never wrongly bounced.
 *
 * Impersonated tutors are gated exactly like real ones, so an admin sees the
 * wizard the tutor sees. The impersonation payload therefore has to carry
 * `onboardingCompleted` (see `mapAuthUserResponse` in backend/src/routes/admin.js),
 * otherwise the gate would behave differently before and after a page reload.
 * The way out stays available: the navbar's "Quitter l'impersonation" button is
 * rendered on the onboarding page too, and /deconnexion is exempt below.
 */
export default function OnboardingGate({
  location,
  children,
}: OnboardingGateProps) {
  const { user, isAuthReady } = useAuth();

  if (!isAuthReady) return <>{children}</>;
  if (!user || user.admin) return <>{children}</>;
  if (user.role !== "tuteur") return <>{children}</>;
  if (user.onboardingCompleted !== false) return <>{children}</>;

  const { pathname } = location;
  const isAllowed =
    pathname.startsWith(frontendRoutes.onboarding) ||
    pathname === frontendRoutes.deconnexion;

  if (isAllowed) return <>{children}</>;

  return <Navigate replace to={frontendRoutes.onboarding} />;
}
